import React, { useState, useRef, useEffect, useMemo } from 'react'
import { useTable, useGlobalFilter, useSortBy } from 'react-table'
import Search from '../../Assets/Images/Departamentos/search.png'
import './table.css'
import {
  Filter,
  EyeSlash,
  Eye,
  ArrowDownUp,
  ArrowDown,
  ArrowUp,
} from 'react-bootstrap-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSquarePen, faTrashCan } from '@fortawesome/free-solid-svg-icons'
import { VAPermissions } from 'Utils/constants'
import { isDummyLitaProduct } from 'Utils/functions'
import useValidatePermission from 'Hooks/useValidatePermission'
import { t } from 'i18next'
import { CustomSwitch } from 'Components/Switch/CustomSwitch.jsx'
import { useFreeTaxes } from 'state/context/CotizationTaxesContext'
import { Formatter } from 'Utils/Formatter'
import { useSelector } from 'react-redux'
import { useCustomerDisplayChannel } from 'Views/CustomerDisplay/hook/useCustomerDisplayChannel'
import { postCustomerDisplayMainMessage } from 'Views/CustomerDisplay/utils/customerDisplayChannel'

const getHiddenColumnKey = columns => [...columns].sort().join(',')

const getToggleableColumns = columns =>
  columns
    .filter(column => column.hideEnabled)
    .map(column => ({ ColumnName: column.Header, Status: column.isVisible }))

function renderFooterCell(
  column,
  indez,
  isProductPage,
  showDiscountOption,
  leftFooterContent
) {
  const noBorder = isProductPage && 'none'
  const hasLeftContent = leftFooterContent !== undefined

  if (column.Header === 'Referencia') {
    return (
      <td
        key={indez}
        style={{
          border: noBorder,
          verticalAlign: 'top',
          width: '18%',
          ...(hasLeftContent
            ? { position: 'relative', overflow: 'visible' }
            : {}),
        }}
        {...column.getFooterProps()}
        data-testid={`text-inventory-footer-reference-${indez}`}>
        {hasLeftContent ? (
          <div style={{ position: 'absolute', top: 0, left: 0, zIndex: 1 }}>
            {leftFooterContent}
          </div>
        ) : (
          column.render('Footer')
        )}
      </td>
    )
  }

  if (column.Header === 'Precio Unitario') {
    return showDiscountOption ? (
      <td
        key={indez}
        style={{ border: noBorder }}
        data-testid={`text-inventory-footer-empty-${indez}`}
      />
    ) : (
      <td
        key={indez}
        style={{ border: noBorder, verticalAlign: 'top' }}
        {...column.getFooterProps()}
        data-testid={`text-inventory-footer-unit-price-${indez}`}>
        {column.render('Footer')}
      </td>
    )
  }

  const isFirstCell = indez === 0
  return (
    <td
      key={indez}
      style={{
        border: noBorder,
        ...(isFirstCell && hasLeftContent ? { verticalAlign: 'top' } : {}),
      }}
      {...column.getFooterProps()}
      data-testid={`text-inventory-footer-cell-${indez}`}>
      {isFirstCell && hasLeftContent
        ? leftFooterContent
        : column.render('Footer')}
    </td>
  )
}

function FilteringTable({
  data,
  prodWithNoCost,
  columns,
  showMargin,
  isProductPage,
  topButtons,
  hasButtons,
  hideFooter,
  isInventory,
  setHiddenColumns,
  hiddenColumns,
  hiddenColumnDiscount,
  handleCartEdit,
  handleCartPopulation,
  generateSalesOrder,
  isPriceNotApproved,
  isSaleOrderGenerated,
  showDiscountOption,
  visibleIcons,
  isDispatchGenerated,
  enableSorting = false,
  handleSortChange,
  isDetailQuote,
  leftFooterContent,
}) {
  const isUserAllowed = useValidatePermission(VAPermissions.createOrder)
  const currentPath = location.pathname
  const { freeTaxes, setFreeTaxes, setIsChanged } = useFreeTaxes()
  const validateSalesOrder =
    isSaleOrderGenerated && !isPriceNotApproved && isUserAllowed
  const useSelectorDetalle = useSelector(state => state.cotizacion.QuoteHeader)

  const channel = useCustomerDisplayChannel()

  const handleSwitchChange = e => {
    setFreeTaxes(e.target.checked)
    setIsChanged(true)
  }

  const seeWarehouseMPD1 = useValidatePermission(
    VAPermissions.seeWareHouse_D1_MP
  )

  const filteredData = useMemo(() => {
    if (!seeWarehouseMPD1) {
      return data?.filter(
        item =>
          !item?.['Código']?.includes('D1') && !item?.['Código']?.includes('MP')
      )
    } else {
      return data
    }
  }, [data, seeWarehouseMPD1])

  const tableInstance = useTable(
    {
      columns,
      data: filteredData,
      initialState: {
        hiddenColumns,
      },
      disableSortRemove: true,
      manualSortBy: Boolean(handleSortChange),
    },
    useGlobalFilter,
    ...(enableSorting ? [useSortBy] : [])
  )
  const inventoryFilter = useRef(null)

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    footerGroups,
    rows,
    prepareRow,
    allColumns,
    state,
    setGlobalFilter,
    setHiddenColumns: setTableHiddenColumns,
  } = tableInstance

  // TABLE LOGIC
  const [showInventoryFilters, setShowInventoryFilters] = useState(false)

  // Stable key to track visibility changes.
  const allColumnsVisibilityKey = allColumns
    .filter(col => col.hideEnabled)
    .map(col => `${col.id}:${col.isVisible}`)
    .join(',')

  const toggleableColumns = useMemo(
    () => getToggleableColumns(allColumns),
    [allColumnsVisibilityKey]
  )
  const hiddenColumnKey = getHiddenColumnKey(hiddenColumns)
  const tableHiddenColumnKey = getHiddenColumnKey(state.hiddenColumns || [])
  const lastPropHiddenColumnKey = useRef(null)
  const isApplyingExternalHiddenColumns = useRef(false)

  // Sync hidden columns after parent-driven updates.
  useEffect(() => {
    const propChanged = lastPropHiddenColumnKey.current !== hiddenColumnKey
    lastPropHiddenColumnKey.current = hiddenColumnKey

    if (propChanged && hiddenColumnKey !== tableHiddenColumnKey) {
      isApplyingExternalHiddenColumns.current = true
      setTableHiddenColumns(hiddenColumns)
    }
  }, [
    hiddenColumnKey,
    tableHiddenColumnKey,
    hiddenColumns,
    setTableHiddenColumns,
  ])

  // GLOBAL FILTER
  function GlobalFilter({ globalFilter, setGlobalFilter }) {
    return (
      <div
        className="inventory-search"
        data-testid="container-inventory-search">
        <input
          value={globalFilter || ''}
          onChange={e => {
            setGlobalFilter(e.target.value)
          }}
          autoFocus
          placeholder="Buscar tienda por nombre o código"
          data-testid="input-inventory-search"
        />
        <img
          className="inv-search-icon"
          src={Search}
          alt="Search icon"
          data-testid="icon-inventory-search"
        />
      </div>
    )
  }

  useEffect(() => {
    const handler = e => {
      if (!inventoryFilter?.current?.contains(e.target)) {
        setShowInventoryFilters(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => {
      document.removeEventListener('mousedown', handler)
    }
  }, [])

  // Report the current visibility state back to the parent.
  useEffect(() => {
    if (isApplyingExternalHiddenColumns.current) {
      if (hiddenColumnKey === tableHiddenColumnKey) {
        isApplyingExternalHiddenColumns.current = false
      }
      return
    }

    const isDiscountVisible = toggleableColumns.some(
      column => column.ColumnName === 'Descuento' && column.Status
    )
    if (hiddenColumnDiscount) {
      hiddenColumnDiscount(isDiscountVisible)
    }
    setHiddenColumns(toggleableColumns)
  }, [
    hiddenColumnKey,
    tableHiddenColumnKey,
    toggleableColumns,
    hiddenColumnDiscount,
    setHiddenColumns,
  ])

  useEffect(() => {
    if (!isDetailQuote) {
      return
    }
    const showDiscount = toggleableColumns.some(
      col => col.ColumnName === 'Descuento' && col.Status
    )
    postCustomerDisplayMainMessage(channel, {
      isDetailQuote,
      quoteData: useSelectorDetalle,
      showDiscount,
      isLoadingDetailQuote: false,
    })
  }, [toggleableColumns, isDetailQuote, channel, useSelectorDetalle])

  useEffect(() => {
    if (state.sortBy && handleSortChange) {
      handleSortChange && handleSortChange(state.sortBy)
    }
  }, [state.sortBy, handleSortChange])

  return (
    <>
      <div
        className="inventory-topbar"
        data-testid="container-inventory-topbar">
        <div
          className="inventory-filter-options"
          data-testid="container-inventory-filter-options">
          <div>
            <button
              className="inventory-filter-button"
              onClick={() => setShowInventoryFilters(!showInventoryFilters)}
              data-testid="btn-toggle-inventory-filters">
              <Filter />
            </button>
            {showInventoryFilters && (
              <div
                ref={inventoryFilter}
                className="inventory-filters"
                data-testid="container-inventory-filters">
                {allColumns.map(column =>
                  column.hideEnabled &&
                  column.id !== 'Tienda' &&
                  column.id !== 'Código' &&
                  column.id !== 'Disponible' ? (
                    <label
                      id={column.id}
                      className="inventory-filter-option"
                      key={column.id}
                      data-testid={`text-inventory-filter-${column.id}`}>
                      <input
                        id={column.id}
                        type="checkbox"
                        {...column.getToggleHiddenProps()}
                        data-testid={`input-inventory-filter-${column.id}`}
                      />
                      {column.id}
                      {column.isVisible ? <Eye /> : <EyeSlash />}
                    </label>
                  ) : null
                )}
              </div>
            )}
          </div>
          {validateSalesOrder && (
            <button
              data-testid="btn-generate-sales-order"
              className={'inventory-sales-order-button ms-2'}
              onClick={generateSalesOrder}>
              {t('GenerateSalesOrder')}
            </button>
          )}

          {isDispatchGenerated &&
            !isPriceNotApproved &&
            isUserAllowed &&
            currentPath.includes('editar_cotizacion') && (
              <>
                <div
                  className="switchContainer"
                  data-testid="container-free-taxes-switch">
                  <CustomSwitch
                    data-testid="switch-free-taxes"
                    checked={freeTaxes}
                    onChange={handleSwitchChange}
                  />
                </div>
                <label
                  className="textSwitchContainer"
                  data-testid="text-free-taxes-switch">
                  {t('FreeTaxQuotations')}
                </label>
              </>
            )}
        </div>
        {isProductPage ? null : (
          <GlobalFilter
            globalFilter={state.globalFilter}
            setGlobalFilter={setGlobalFilter}
          />
        )}
        {hasButtons ? topButtons : null}
      </div>
      <div
        className={`scrollable ${isInventory ? 'tableContainer' : ''}`}
        data-testid="container-inventory-table-scroll">
        <table
          className="inventory-table"
          {...getTableProps()}
          data-testid="table-inventory">
          <thead data-testid="container-inventory-table-head">
            {headerGroups.map((headerGroup, index) => (
              <tr
                key={index}
                {...headerGroup.getHeaderGroupProps()}
                data-testid={`item-inventory-header-row-${index}`}>
                {headerGroup.headers.map((column, indez) => (
                  <th
                    key={indez}
                    {...column.getHeaderProps({
                      ...(enableSorting && column.getSortByToggleProps
                        ? column.getSortByToggleProps()
                        : {}),
                      title: undefined,
                    })}
                    data-testid={`text-inventory-header-${column.id || indez}`}>
                    <div
                      className="order-header-table-container"
                      data-testid={`container-inventory-header-${
                        column.id || indez
                      }`}>
                      <span
                        data-testid={`text-inventory-header-label-${
                          column.id || indez
                        }`}>
                        {column.render('Header')}
                      </span>
                      {enableSorting && !column.disableSortBy && (
                        <span
                          style={{
                            fontSize: '14px',
                            marginLeft: '9px',
                            transition: 'opacity 0.2s',
                            color: column.isSorted
                              ? 'var(--secondary-color)'
                              : 'var(--input-background-color)',
                          }}
                          data-testid={`btn-sort-inventory-${
                            column.id || indez
                          }`}>
                          {column.isSorted ? (
                            column.isSortedDesc ? (
                              <ArrowDown />
                            ) : (
                              <ArrowUp />
                            )
                          ) : (
                            <ArrowDownUp />
                          )}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody
            {...getTableBodyProps()}
            data-testid="container-inventory-table-body">
            {rows.map((row, index) => {
              prepareRow(row)
              const isRowDummyProduct = isDummyLitaProduct(row.original)
              return (
                <tr
                  key={index}
                  {...row.getRowProps()}
                  data-testid={`item-inventory-row-${index}`}
                  style={
                    isRowDummyProduct
                      ? {
                          backgroundColor: 'var(--error-background-color)',
                          border: '2px solid var(--error-color)',
                        }
                      : undefined
                  }>
                  {row.cells.map((cell, indez) => {
                    return cell.column.Header === 'Margen' ? (
                      <td
                        {...cell.getCellProps()}
                        data-testid={`text-inventory-margin-${index}-${indez}`}>
                        {showMargin ? cell.value : '****'}
                      </td>
                    ) : cell.column.Header === 'Imagen' ? (
                      <td
                        {...cell.getCellProps({
                          style: {
                            maxWidth: '200px',
                          },
                        })}
                        data-testid={`container-inventory-product-image-${index}-${indez}`}>
                        <img
                          src={cell.value}
                          alt="product-image"
                          style={{ maxWidth: '50px' }}
                          data-testid={`img-inventory-product-${index}-${indez}`}
                        />
                      </td>
                    ) : cell.column.Header === 'Precio Unitario' &&
                      !showDiscountOption ? (
                      <td
                        {...cell.getCellProps()}
                        style={
                          prodWithNoCost?.some(
                            prod => prod.Sku === cell.row.original.Referencia
                          )
                            ? { color: 'red' }
                            : {}
                        }
                        data-testid={`text-inventory-unit-price-${index}-${indez}`}>
                        $ {Formatter.Money(cell.value)}
                      </td>
                    ) : cell.column.Header === 'Precio Unitario' ||
                      cell.column.Header === 'Descuento' ? (
                      <td
                        {...cell.getCellProps()}
                        style={
                          prodWithNoCost?.some(
                            prod => prod.Sku === cell.row.original.Referencia
                          )
                            ? { color: 'red' }
                            : {}
                        }
                        data-testid={`text-inventory-price-discount-${index}-${indez}`}>
                        $ {Formatter.Money(cell.value)}
                      </td>
                    ) : cell.column.Header === 'Precio Total' ? (
                      <td
                        {...cell.getCellProps()}
                        style={
                          prodWithNoCost?.some(
                            prod => prod.Sku === cell.row.original.Referencia
                          )
                            ? { color: 'red' }
                            : {}
                        }
                        data-testid={`text-inventory-total-price-${index}-${indez}`}>
                        ${' '}
                        {Formatter.Money(cell.value, {
                          decimalPrecision: 2,
                          rounded: true,
                        })}
                      </td>
                    ) : cell.column.Header === 'Acciones' ? (
                      <td
                        key={cell.value}
                        {...cell.getCellProps()}
                        data-testid={`container-inventory-actions-cell-${index}-${indez}`}>
                        <div
                          className="inventory-actions"
                          data-testid={`container-inventory-actions-${index}`}>
                          <FontAwesomeIcon
                            icon={faSquarePen}
                            onClick={() => {
                              handleCartPopulation()
                            }}
                            style={{
                              display: hasButtons,
                              fontSize: '2.5rem',
                            }}
                            data-testid={`btn-edit-inventory-row-${index}`}
                          />
                          <FontAwesomeIcon
                            style={{
                              display: hasButtons,
                              color: '#F64E60',
                              fontSize: '2rem',
                            }}
                            icon={faTrashCan}
                            onClick={() => {
                              handleCartEdit(cell.row.original, cell.row.id)
                            }}
                            data-testid={`btn-delete-inventory-row-${index}`}
                          />
                        </div>
                      </td>
                    ) : (
                      <td
                        {...cell.getCellProps()}
                        style={
                          prodWithNoCost?.some(
                            prod => prod.Sku === cell.row.original.Referencia
                          )
                            ? { color: 'red' }
                            : {}
                        }
                        data-testid={`text-inventory-cell-${index}-${indez}`}>
                        {cell.render('Cell')}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
          {hideFooter ? null : (
            <tfoot
              className={isInventory ? 'stickyFooter' : null}
              style={{
                borderBottom: isProductPage && 'none',
                borderLeft: isProductPage && 'none',
                borderRight: isProductPage && 'none',
              }}
              data-testid="container-inventory-table-footer">
              {footerGroups.map((group, index) => (
                <tr
                  key={index}
                  {...group.getFooterGroupProps()}
                  className="hidde-border-bottom"
                  data-testid={`item-inventory-footer-row-${index}`}>
                  {group.headers.map((column, indez) =>
                    renderFooterCell(
                      column,
                      indez,
                      isProductPage,
                      showDiscountOption,
                      leftFooterContent
                    )
                  )}
                </tr>
              ))}
            </tfoot>
          )}
        </table>
      </div>
    </>
  )
}
export default FilteringTable
